"use client"

import { useRef, useState } from "react"
import { useBranding } from "@/hooks/useBranding"
import { cn } from "@/lib/utils"

/**
 * The tenant brand logo, theme-aware.
 *
 * ── Why CSS and not `useTheme().resolvedTheme` ────────────────────────────
 * next-themes writes the `.dark` class onto <html> from a BLOCKING inline
 * script in <head>, so the class is correct before the first paint. A CSS
 * `dark:` variant therefore shows the right logo on frame one. Reading
 * `resolvedTheme` in React cannot: it is `undefined` until next-themes' own
 * mount effect runs, so every cold load in dark theme would paint the LIGHT
 * logo for a frame and then swap — a visible flash on exactly the signed-out
 * screens (welcome / login / signup) where the logo is the largest element on
 * the page. That also makes this correct under SSR, with JS disabled, and
 * during hydration.
 *
 * ── Why it collapses to one <img> ─────────────────────────────────────────
 * `hasThemedLogo` is false unless the tenant configured a distinct
 * `logo_url_dark`. Every tenant onboarded before theme-aware branding falls
 * into that branch and renders exactly the markup they rendered before — one
 * <img>, same src, same classes. The dual-<img> path is opt-in per tenant.
 *
 * Both sources are already inlined as base64 by useBranding, so rendering two
 * <img> tags costs no extra network request.
 */
export function BrandLogo({
  className,
  fallback = null,
  onError,
}: {
  /** Applied to the <img> (both of them, in the themed case). */
  className?: string
  /** Rendered instead of the logo if the image fails to load. */
  fallback?: React.ReactNode
  /** Notified once when EVERY source has failed, for callers whose surrounding
   *  LAYOUT changes on failure (SidebarBrand swaps between a wide-wordmark
   *  and an icon+text arrangement) and so cannot express the fallback as a
   *  drop-in node. */
  onError?: () => void
}) {
  const { appName, logoUrl, logoUrlDark, hasThemedLogo } = useBranding()
  // Tracked per SOURCE, not once for the component. A themed tenant's two URLs
  // are independent and frequently on different hosts, so one 404 must not
  // blank out the logo in the theme whose URL is perfectly healthy.
  const [lightFailed, setLightFailed] = useState(false)
  const [darkFailed, setDarkFailed] = useState(false)
  // Both <img> error events can fire in the same tick, before React re-renders.
  // Deciding "is everything exhausted?" from state alone would let each handler
  // still see the other source as healthy and skip the callback entirely, so
  // the synchronous view of failure lives in a ref.
  const failedRef = useRef({ light: false, dark: false })

  // Only fire once nothing is left to render — reporting on the FIRST of two
  // failures would throw away a logo we can still show.
  const reportIfExhausted = () => {
    const { light, dark } = failedRef.current
    if (light && (dark || !hasThemedLogo)) onError?.()
  }
  const handleLightError = () => {
    failedRef.current.light = true
    setLightFailed(true)
    reportIfExhausted()
  }
  const handleDarkError = () => {
    failedRef.current.dark = true
    setDarkFailed(true)
    reportIfExhausted()
  }

  // Single-logo tenant (and the bundled default wordmark) — unchanged markup.
  if (!hasThemedLogo) {
    if (lightFailed) return <>{fallback}</>
    return <img src={logoUrl} alt={appName} onError={handleLightError} className={className} />
  }

  if (lightFailed && darkFailed) return <>{fallback}</>

  // Exactly one source died. Degrade to the SURVIVOR in both themes rather
  // than to `fallback`: showing the tenant's remaining logo everywhere is the
  // same single-logo behaviour every other tenant already gets, and it keeps
  // the caller's layout intact. If the survivor fails too we land in the
  // branch above.
  if (lightFailed || darkFailed) {
    const src = lightFailed ? logoUrlDark : logoUrl
    const handler = lightFailed ? handleDarkError : handleLightError
    return <img src={src} alt={appName} onError={handler} className={className} />
  }

  // Themed tenant, both sources healthy. Both tags are always in the DOM; CSS
  // picks one. Each carries the real `alt` — `display:none` already removes
  // the hidden twin from the accessibility tree, so the brand name is
  // announced exactly once, in whichever theme is active.
  return (
    <>
      <img
        src={logoUrl}
        alt={appName}
        onError={handleLightError}
        className={cn(className, "dark:hidden")}
      />
      <img
        src={logoUrlDark}
        alt={appName}
        onError={handleDarkError}
        className={cn(className, "hidden dark:block")}
      />
    </>
  )
}
